'use client';

import { useState } from 'react';
import { CheckCircledIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';

import { useAppEvents } from '@kit/shared/events';
import { useSignInWithOtp } from '@kit/supabase/hooks/use-sign-in-with-otp';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { If } from '@kit/ui/if';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import { usePasswordSignUpFlow } from '../hooks/use-sign-up-flow';
import { isAccountExistsError } from '../lib/auth-error-matchers';
import { AuthErrorAlert } from './auth-error-alert';
import { AuthRecoveryHint } from './auth-recovery-hint';
import { PasswordSignUpForm } from './password-sign-up-form';

interface EmailPasswordSignUpContainerProps {
  displayTermsCheckbox?: boolean;
  defaultValues?: {
    email: string;
  };
  onSignUp?: (userId?: string) => unknown;
  emailRedirectTo: string;
  captchaSiteKey?: string;
  /**
   * When true, the form offers a magic-link alternative behind a quiet
   * secondary action that reuses the same email field, instead of rendering a
   * separate stacked magic-link form with a duplicate Terms checkbox.
   */
  magicLinkEnabled?: boolean;
  /**
   * Optional metadata to attach to the user at signup (e.g. brand of
   * origin so future analytics + brand-aware onboarding can read
   * `user.user_metadata.signup_brand`).
   */
  signupMetadata?: Record<string, unknown>;
  /**
   * Callback + return paths for the directed recovery hint's one-click
   * provider button (shown when the email already exists on an OAuth-only
   * account). Omit to fall back to a plain "Sign in" link.
   */
  oauthPaths?: { callback: string; returnPath: string };
  /** Where the recovery hint's "Sign in" fallback link points. */
  signInPath?: string;
}

export function EmailPasswordSignUpContainer({
  defaultValues,
  onSignUp,
  emailRedirectTo,
  displayTermsCheckbox,
  captchaSiteKey,
  magicLinkEnabled = false,
  signupMetadata,
  oauthPaths,
  signInPath,
}: EmailPasswordSignUpContainerProps) {
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const captchaLoading = !captcha.isReady;
  const { t } = useTranslation();
  const appEvents = useAppEvents();
  const { recordAuthMethod } = useLastAuthMethod();

  // The email of the most recent attempt, so a failure can be turned into a
  // directed recovery action (e.g. "you already have a Google account").
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    signUp: onSignupRequested,
    loading,
    error,
    showVerifyEmailAlert,
  } = usePasswordSignUpFlow({
    emailRedirectTo,
    onSignUp,
    captchaToken: captcha.token,
    resetCaptchaToken: captcha.reset,
    signupMetadata,
  });

  const magicLinkMutation = useSignInWithOtp();

  const onMagicLinkSubmit = (email: string) => {
    // Funnel entry — see the `user.signUpStarted` docstring in
    // `@kit/shared/events`. Emitted before the send so an attempt that fails
    // (captcha, rate limit) still counts against the completion event below.
    appEvents.emit({
      type: 'user.signUpStarted',
      payload: {
        method: 'magiclink',
      },
    });

    const promise = async () => {
      await magicLinkMutation.mutateAsync({
        email,
        options: {
          emailRedirectTo,
          captchaToken: captcha.token,
          shouldCreateUser: true,
        },
      });

      recordAuthMethod('magic_link', { email });

      appEvents.emit({
        type: 'user.signedUp',
        payload: {
          method: 'magiclink',
        },
      });
    };

    toast.promise(promise, {
      loading: t('auth:sendingEmailLink'),
      success: t('auth:sendLinkSuccessToast'),
      error: t('auth:errors.linkTitle'),
    });

    captcha.reset();
  };

  return (
    <>
      <If condition={showVerifyEmailAlert}>
        <SuccessAlert />
      </If>

      <If condition={!showVerifyEmailAlert}>
        <AuthErrorAlert error={error} />

        <If
          condition={isAccountExistsError(error) && submittedEmail.length > 0}
        >
          <AuthRecoveryHint
            email={submittedEmail}
            context={'sign-up'}
            signInPath={signInPath}
            oauthPaths={oauthPaths}
          />
        </If>

        <div>
          <PasswordSignUpForm
            onSubmit={(params) => {
              setSubmittedEmail(params.email);
              return onSignupRequested(params);
            }}
            loading={loading}
            defaultValues={defaultValues}
            displayTermsCheckbox={displayTermsCheckbox}
            captchaLoading={captchaLoading}
            magicLinkEnabled={magicLinkEnabled}
            onMagicLinkSubmit={onMagicLinkSubmit}
            magicLinkLoading={magicLinkMutation.isPending}
          />

          {captcha.field}
        </div>
      </If>
    </>
  );
}

function SuccessAlert() {
  return (
    <Alert variant={'success'}>
      <CheckCircledIcon className={'w-4'} />

      <AlertTitle>
        <Trans i18nKey={'auth:emailConfirmationAlertHeading'} />
      </AlertTitle>

      <AlertDescription data-test={'email-confirmation-alert'}>
        <Trans i18nKey={'auth:emailConfirmationAlertBody'} />
      </AlertDescription>
    </Alert>
  );
}
